import { Injectable, Injector, OnDestroy } from '@angular/core';

import {
    EMPTY,
    Observable,
    Subject,
    Subscription,
    filter,
    fromEvent,
    map,
    merge,
    share,
    switchMap,
    tap,
    throttleTime,
    timer
} from 'rxjs';

import { CoreDialogService } from '../../Core/Dialogs/services/dialog.service';
import { ConfigService } from '../../Core/Config/services/config.service';


enum SessionManagementExpiryBroadcastType {
    QUERY = 'query',
    RESPONSE = 'response',
    EXPIRING = 'expiring',
    LOGOUT = 'logout',
    REFRESH = 'refresh'
}

interface SessionManagementBroadcastMessage {
    key: string;
    type: SessionManagementExpiryBroadcastType;
    mostRecentTimestamp?: Date;
}

/**
 * active - user known to be active in this tab or another
 * pending - user activity in this tab is about to expire
 * expiring - user activity has lapsed, show the warning dialog
 * expired - user activity has lapsed and not been refreshed - logout and show session expired dialog
 * unauthenticated - user has yet to sign in or has logged out explicitly
 */
export enum SessionStateEnum {
    ACTIVE,
    PENDING,
    EXPIRING,
    EXPIRED,
    UNAUTHENTICATED,
}

enum StateSourceEnum {
    USER_ACTION,
    WARNING_DIALOG,
    EXPIRED_DIALOG,
    LOGIN,
    QUERY,
    TIMER,
    REFRESH,
    INTERCEPTOR,
}
enum StateUpdateOriginEnum {
    INTERNAL,
    EXTERNAL
}
interface StateUpdate {
    newState: SessionStateEnum;
    source: StateSourceEnum;
    origin: StateUpdateOriginEnum;
    heartbeatDate?: Date;
}


/**
 * TODO: Investigate SharedWorkers instead of BroadcastChannels
 */
@Injectable()
export class SessionManagementService implements OnDestroy {
    static readonly SESSION_MANAGEMENT_MESSAGE_ID = 'SessionManagement';
    static readonly SESSION_MANAGEMENT_EXPIRY_WARNING_KEY = 'SessionManagementExpiryWarningDialog';
    static readonly SESSION_START_REASON_INITIAL_LOGIN = 'initial_login';
    static readonly SESSION_START_REASON_RE_AUTHENTICATION = 're_authentication';

    private readonly config = {
        /** The minimum amount of time between heartbeats, given some user activity */
        HEARTBEAT_THROTTLE_MS: 10 * 1000, // 10 second throttle
        /** The amount of time a user is allowed to be inactive, shown in the warning dialog, before being logged out */
        TIMEOUT_MS: 60 * 60 * 1000, // 60 min timeout
        /** The short pause between the timeout and actual logging out, to allow for broadcast updates */
        EXPIRY_CHECK_MS: 3 * 1000, // 3 seconds
        /** The minimum amount of time between heartbeat api calls to the backend */
        HEARTBEAT_CALL_THROTTLE_MS: 5 * 60 * 1000, // 5 minute throttle
    };

    private readonly subscriptions: Subscription[] = [];

    private currentState: SessionStateEnum = SessionStateEnum.UNAUTHENTICATED;

    private showSessionWarning$ = new Subject<void>();
    private userSessionExpiredSubject$ = new Subject<{ performLogout: boolean }>();
    private userIsLoggedOut$ = new Subject<void>();
    private expiredSessionRefreshedSubject$ = new Subject<void>();
    private sessionStartedSubject$ = new Subject<{ reason: string }>();
    private sessionWarningShownSubject$ = new Subject<void>();

    /** Used by internal services to influence current state */
    private sessionStateInternal$ = new Subject<StateUpdate>();

    private mostRecentActivityTimestamp: Date = new Date();

    private click$ = fromEvent(document, 'click');
    private keyup$ = fromEvent(document, 'keyup');
    private userAction$ = merge(
        this.click$,
        this.keyup$,
    );

    /** Heartbeat caused by user action in this tab */
    private userActionHeartbeat$ = this.userAction$.pipe(
        throttleTime(this.config.HEARTBEAT_THROTTLE_MS),
    );

    /**
     * User Actions and timer events produce state update, which are handled here
     *
     * TODO: Could use some kind of config object to handle state changes
     */
    private state$ = merge(
        this.userActionHeartbeat$.pipe(
            map(() => {
                return {
                    newState: SessionStateEnum.ACTIVE,
                    origin: StateUpdateOriginEnum.INTERNAL,
                    source: StateSourceEnum.USER_ACTION,
                    heartbeatDate: new Date()
                };
            })
        ),
        this.sessionStateInternal$,
    ).pipe(
        map(({ newState, origin, source, heartbeatDate }) => {
            if (source === StateSourceEnum.USER_ACTION) {
                // Clicks and keyboard events
                if (this.currentState === SessionStateEnum.PENDING || this.currentState === SessionStateEnum.ACTIVE) {
                    this.mostRecentActivityTimestamp = heartbeatDate;
                    this.currentState = SessionStateEnum.ACTIVE;
                }
            } else if (source === StateSourceEnum.QUERY) {
                // Query response
                if (this.currentState === SessionStateEnum.PENDING && heartbeatDate > this.mostRecentActivityTimestamp) {
                    this.mostRecentActivityTimestamp = heartbeatDate;
                    this.currentState = SessionStateEnum.ACTIVE;
                }
            } else if (source === StateSourceEnum.WARNING_DIALOG) {
                if (this.currentState === SessionStateEnum.EXPIRING) {
                    if (newState === SessionStateEnum.ACTIVE) {
                        // Session extended by user via the warning dialog
                        this.mostRecentActivityTimestamp = new Date();
                        this.closeWarningDialog();
                        this.broadcastSessionRefresh();
                        this.currentState = SessionStateEnum.ACTIVE;
                    } else if (newState === SessionStateEnum.EXPIRED) {
                        // User failed to extend session, either here or in another dialog
                        if (origin === StateUpdateOriginEnum.EXTERNAL) {
                            this.currentState = SessionStateEnum.EXPIRED;
                            this.closeWarningDialog();
                            this.userSessionExpiredSubject$.next({ performLogout: false });
                        } else {
                            this.currentState = SessionStateEnum.EXPIRED;
                            this.closeWarningDialog();
                            this.broadcastSessionLogout();
                            this.userSessionExpiredSubject$.next({ performLogout: true });
                        }
                    }
                }
            } else if (source === StateSourceEnum.LOGIN) {
                // User in this tab has logged in or re-logged in
                if (this.currentState === SessionStateEnum.EXPIRED) {
                    this.mostRecentActivityTimestamp = new Date();
                    this.broadcastSessionRefresh();
                    this.currentState = SessionStateEnum.ACTIVE;
                    this.sessionStartedSubject$.next({ reason: SessionManagementService.SESSION_START_REASON_RE_AUTHENTICATION });
                } else if (this.currentState === SessionStateEnum.UNAUTHENTICATED) {
                    this.mostRecentActivityTimestamp = new Date();
                    this.currentState = SessionStateEnum.ACTIVE;
                    this.sessionStartedSubject$.next({ reason: SessionManagementService.SESSION_START_REASON_INITIAL_LOGIN });
                }
            } else if (source === StateSourceEnum.REFRESH) {
                // Another tab has had its session refreshed or extended
                this.mostRecentActivityTimestamp = new Date();
                if (this.currentState === SessionStateEnum.EXPIRING) {
                    this.closeWarningDialog();
                } else if (this.currentState === SessionStateEnum.EXPIRED) {
                    this.expiredSessionRefreshedSubject$.next();
                }
                this.currentState = SessionStateEnum.ACTIVE;
            } else if (source === StateSourceEnum.TIMER && origin === StateUpdateOriginEnum.INTERNAL) {
                // Query for more recent activity failed
                if (this.currentState === SessionStateEnum.PENDING) {
                    this.showSessionWarning$.next();
                    this.broadcastSessionExpiry();
                    this.currentState = SessionStateEnum.EXPIRING;
                }
            } else if (source === StateSourceEnum.TIMER && origin === StateUpdateOriginEnum.EXTERNAL) {
                // Another tab's query failed - show our warning dialog
                if (this.currentState === SessionStateEnum.ACTIVE || this.currentState === SessionStateEnum.PENDING) {
                    this.showSessionWarning$.next();
                    this.currentState = SessionStateEnum.EXPIRING;
                }
            } else if (source === StateSourceEnum.INTERCEPTOR) {
                // Interceptor or ExpiredDialog event
                if (newState === SessionStateEnum.EXPIRED) {
                    this.currentState = SessionStateEnum.EXPIRED;
                } else if (newState === SessionStateEnum.UNAUTHENTICATED) {
                    this.currentState = SessionStateEnum.UNAUTHENTICATED;
                }
            }
        })
    );

    private heartbeat$ = this.state$.pipe(
        filter(() => this.currentState !== SessionStateEnum.PENDING), // If pending, we are waiting for query responses
        switchMap(() => {
            if (this.currentState !== SessionStateEnum.ACTIVE) {
                // We want to switch to EMPTY (rather than filter the emission out above) to ensure the timer below is cancelled
                return EMPTY;
            }
            return timer(this.config.TIMEOUT_MS).pipe(
                tap(() => this.currentState = SessionStateEnum.PENDING),
                tap(() => {
                    this.queryForLatestActivity();
                }),
                switchMap(() => timer(this.config.EXPIRY_CHECK_MS)),
                tap(() => this.sessionStateInternal$.next({
                    newState: SessionStateEnum.EXPIRING,
                    origin: StateUpdateOriginEnum.INTERNAL,
                    source: StateSourceEnum.TIMER,
                }))
            );
        }),
        share(),
    );


    private sessionBroadcastChannel: BroadcastChannel;
    private dialogService: CoreDialogService;
    private heartbeatSubscription: Subscription;


    /** Make an api call to keep the backend session going */
    heartbeatCall$ = this.userActionHeartbeat$.pipe(
        filter(() => this.currentState === SessionStateEnum.ACTIVE),
        throttleTime(this.config.HEARTBEAT_CALL_THROTTLE_MS),
    );

    userSessionExpired$ = this.userSessionExpiredSubject$.asObservable();

    expiredSessionRefreshed$ = this.expiredSessionRefreshedSubject$.asObservable();

    /** Fires when the user successfully authenticates (initial login or re-auth after expiry) */
    sessionStarted$ = this.sessionStartedSubject$.asObservable();

    /** Fires when the session expiry warning dialog is shown */
    sessionWarningShown$ = this.sessionWarningShownSubject$.asObservable();

    constructor(
        // We can't inject the coreDialog directly as that results in a circular dependency, inject it later
        private injector: Injector,
        private configService: ConfigService,
    ) {
        this.subscriptions.push(
            this.showSessionWarning$.pipe(
                tap(() => this.sessionWarningShownSubject$.next()),
                switchMap(() => {
                    this.setDialogService();
                    return this.openExpiryConfirmDialog();
                })
            ).subscribe()
        );

        this.heartbeatSubscription = this.heartbeat$.subscribe();

        const config = this.configService.getConfig();
        if (config.sessionTimeoutInMinutes && config.sessionTimeoutInMinutes > 0) {
            this.config.TIMEOUT_MS = config.sessionTimeoutInMinutes * 60 * 1000;
            this.heartbeatSubscription?.unsubscribe();
            this.heartbeatSubscription = this.heartbeat$.subscribe();
        }

        this.sessionBroadcastChannel = new BroadcastChannel(SessionManagementService.SESSION_MANAGEMENT_MESSAGE_ID);
        const listener = e => {
            if (e.origin !== window.location.origin || e.data.key !== SessionManagementService.SESSION_MANAGEMENT_MESSAGE_ID) {
                return;
            }

            const result: SessionManagementBroadcastMessage = e.data;
            switch (result.type) {
                case SessionManagementExpiryBroadcastType.QUERY:
                    this.respondToLatestActivityQuery();
                    break;
                case SessionManagementExpiryBroadcastType.RESPONSE:
                    // Another tab has responded to our query
                    this.sessionStateInternal$.next({
                        newState: SessionStateEnum.ACTIVE,
                        origin: StateUpdateOriginEnum.EXTERNAL,
                        source: StateSourceEnum.QUERY,
                        heartbeatDate: result.mostRecentTimestamp
                    });
                    break;
                case SessionManagementExpiryBroadcastType.EXPIRING:
                    // Another tab session is expiring
                    this.sessionStateInternal$.next({
                        newState: SessionStateEnum.EXPIRING,
                        origin: StateUpdateOriginEnum.EXTERNAL,
                        source: StateSourceEnum.TIMER,
                    });
                    break;
                case SessionManagementExpiryBroadcastType.LOGOUT:
                    // Another tab session has expired and logged out
                    this.sessionStateInternal$.next({
                        newState: SessionStateEnum.EXPIRED,
                        origin: StateUpdateOriginEnum.EXTERNAL,
                        source: StateSourceEnum.WARNING_DIALOG,
                    });
                    break;
                case SessionManagementExpiryBroadcastType.REFRESH:
                    // Another tab session has been refreshed
                    this.sessionStateInternal$.next({
                        newState: SessionStateEnum.ACTIVE,
                        origin: StateUpdateOriginEnum.EXTERNAL,
                        source: StateSourceEnum.REFRESH,
                        heartbeatDate: new Date()
                    });
                    break;
            }
        };
        this.sessionBroadcastChannel.addEventListener ('message', listener);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.heartbeatSubscription?.unsubscribe();
    }

    getUserIsLoggedOut$(): Observable<void> {
        return this.userIsLoggedOut$;
    }

    handleSessionRefreshAfterExpiry() {
        this.sessionStateInternal$.next({
            newState: SessionStateEnum.ACTIVE,
            origin: StateUpdateOriginEnum.INTERNAL,
            source: StateSourceEnum.LOGIN,
            heartbeatDate: new Date()
        });
    }

    handleLogOut() {
        if (this.currentState !== SessionStateEnum.UNAUTHENTICATED) {
            return;
        }
        const currentQueryParams = new URLSearchParams(window.location.search);
        let returnUrl: string;
        if (currentQueryParams && currentQueryParams.get('returnUrl')) {
            returnUrl = currentQueryParams.get('returnUrl');
        } else if (
            window.location.href.toLowerCase().indexOf('login') === -1 &&
            // handles cases like app.agilitypr.com with no angular level path specified, we don't need to redirect back here
            window.location.pathname !== '/'
        ) {
            returnUrl = window.location.href;
        }
        const newQueryParams = {
            'returnUrl': returnUrl
        };

        if (currentQueryParams && currentQueryParams.get('noRedirect') === '') {
            newQueryParams['noRedirect'] = '';
        }

        this.userIsLoggedOut$.next();
    }

    setSessionState(newState: SessionStateEnum) {
        if (
            (this.currentState === SessionStateEnum.UNAUTHENTICATED || this.currentState === SessionStateEnum.EXPIRED)
            && newState === SessionStateEnum.ACTIVE
        ) {
            this.sessionStateInternal$.next({
                newState: SessionStateEnum.ACTIVE,
                origin: StateUpdateOriginEnum.INTERNAL,
                source: StateSourceEnum.LOGIN,
                heartbeatDate: new Date()
            });
        } else if (newState === SessionStateEnum.UNAUTHENTICATED || newState === SessionStateEnum.EXPIRED) {
            this.sessionStateInternal$.next({
                newState: newState,
                origin: StateUpdateOriginEnum.INTERNAL,
                source: StateSourceEnum.INTERCEPTOR,
            });
        }
    }

    private openExpiryConfirmDialog(): Observable<void> {
        return this.dialogService.openWithObservable(
            'SessionExpiryConfirmDialogComponent',
            {
                inputs: {},
                outputs: {
                    timerExpired: () => {
                        this.sessionStateInternal$.next({
                            newState: SessionStateEnum.EXPIRED,
                            origin: StateUpdateOriginEnum.INTERNAL,
                            source: StateSourceEnum.WARNING_DIALOG,
                        });
                    },
                    sessionExtended: () => {
                        this.sessionStateInternal$.next({
                            newState: SessionStateEnum.ACTIVE,
                            origin: StateUpdateOriginEnum.INTERNAL,
                            source: StateSourceEnum.WARNING_DIALOG,
                            heartbeatDate: new Date()
                        });
                    },
                    dialogClose: (confirmed: boolean) => {
                        if (confirmed) {
                            this.sessionStateInternal$.next({
                                newState: SessionStateEnum.ACTIVE,
                                origin: StateUpdateOriginEnum.INTERNAL,
                                source: StateSourceEnum.WARNING_DIALOG,
                                heartbeatDate: new Date()
                            });
                        }
                    }
                }
            },
            null,
            SessionManagementService.SESSION_MANAGEMENT_EXPIRY_WARNING_KEY
        );
    }

    private broadcastSessionRefresh() {
        const query: SessionManagementBroadcastMessage = {
            key: SessionManagementService.SESSION_MANAGEMENT_MESSAGE_ID,
            type: SessionManagementExpiryBroadcastType.REFRESH,
            mostRecentTimestamp: this.mostRecentActivityTimestamp
        };
        this.sessionBroadcastChannel.postMessage(query);
    }

    private broadcastSessionLogout() {
        const query: SessionManagementBroadcastMessage = {
            key: SessionManagementService.SESSION_MANAGEMENT_MESSAGE_ID,
            type: SessionManagementExpiryBroadcastType.LOGOUT,
        };
        this.sessionBroadcastChannel.postMessage(query);
    }

    private broadcastSessionExpiry() {
        const query: SessionManagementBroadcastMessage = {
            key: SessionManagementService.SESSION_MANAGEMENT_MESSAGE_ID,
            type: SessionManagementExpiryBroadcastType.EXPIRING,
        };
        this.sessionBroadcastChannel.postMessage(query);
    }

    private queryForLatestActivity() {
        const query: SessionManagementBroadcastMessage = {
            key: SessionManagementService.SESSION_MANAGEMENT_MESSAGE_ID,
            type: SessionManagementExpiryBroadcastType.QUERY,
        };
        this.sessionBroadcastChannel.postMessage(query);
    }

    private respondToLatestActivityQuery() {
        const query: SessionManagementBroadcastMessage = {
            key: SessionManagementService.SESSION_MANAGEMENT_MESSAGE_ID,
            type: SessionManagementExpiryBroadcastType.RESPONSE,
            mostRecentTimestamp: this.mostRecentActivityTimestamp
        };
        this.sessionBroadcastChannel.postMessage(query);
    }

    private setDialogService() {
        if (!this.dialogService) {
            this.dialogService = this.injector.get(CoreDialogService);
        }
    }

    private closeWarningDialog() {
        this.setDialogService();
        this.dialogService.close(SessionManagementService.SESSION_MANAGEMENT_EXPIRY_WARNING_KEY);
    }
}
